import React from 'react';
import { X, CheckCircle, ArrowRight } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SplashContainer } from './splash.styled';
import { useSplash } from '../../contexts/SplashContext';

interface IntroSplashProps {
  onClose: () => void;
}

/**
 * IntroSplash Component
 * Welcome/upgrade splash screen shown once per login session
 * when user first opens the hotel questionnaire
 */
const IntroSplash: React.FC<IntroSplashProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const hotelId = path.split('/')[2];
  const { clearQueue } = useSplash();

  const handleGoToBoost = () => {
    clearQueue(); // Clear queued splashes before navigation
    onClose();
    navigate(`/upgrade?hotelId=${hotelId}`);
  };
  return (
    <SplashContainer className="animate-fadeIn">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        style={{ zIndex: -1 }}
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto overflow-hidden animate-slideUp">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
          aria-label="Close splash"
        >
          <X size={24} />
        </button>

        {/* Content Section */}
        <div className="px-8 py-10">
          {/* Greeting */}
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Hello there!
          </h2>

          {/* Main Message */}
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            Your sustainability data is a key sales driver. But not on its own.
          </p>

          {/* Boost Intro */}
          <div className="mb-6">
            <p className="text-lg text-gray-900 font-semibold mb-4">
              Alō Index Boost gives you the tools you need to leverage
              sustainability to compound sales:
            </p>

            {/* Benefits List */}
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
                </div>
                <p className="text-gray-700">
                  Save time with autocomplete and one-click data sharing
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
                </div>
                <p className="text-gray-700">
                  Market to travelers directly in their approved booking tool
                </p>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
                </div>
                <p className="text-gray-700">
                  Build your pipeline with a Global Directory listing
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleGoToBoost}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
              style={{ backgroundColor: 'var(--color-primary-600)' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-700)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-600)'}
            >
              Check out Boost
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* What if I don't Boost Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              What happens if I don't Boost?
            </h3>
            <p className="text-gray-600 mb-4">
              You will have submit-only access and be able to update your data
              annually. You will not have access to any of the benefits
              mentioned above.
            </p>
          </div>

          {/* Continue Without Boost Button */}
          <button
            onClick={onClose}
            className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
          >
            CONTINUE WITHOUT BOOST
          </button>
        </div>
      </div>
    </SplashContainer>
  );
};

export default IntroSplash;
